import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Company } from '../../stores/types';
// Components
import { EmailOutline, ErrorWarningCircle, ExternalLink } from '../../assets/icons';
import { IconSize } from '../../constant/IconSize.constant';
import { useAuth, USER_ROLE } from '../../hooks';
import { useCompany } from '../../hooks/useCompany';
import { useCompanyPermissions } from '../../hooks/useCompanyPermissions';
import { useNavigateWithSearchParam } from '../../hooks/useNavigateWithSearchParam';
import {
  useSendBankInfoReminderMutation,
  useSyncWithHubSpotMutation,
} from '../../services/company.api';
import {
  BankInfoProvided,
  sendBankInfoReminderPayload,
} from '../../stores/types/company.interface';
import { FeePackage, PackageType } from '../../stores/types/feePackage.interface';
import { convertToLocaleCurrency } from '../../utils/Currency.Util';
import {
  Button,
  ButtonSize,
  ButtonType,
  Card,
  ColorType,
  Icon,
  Label,
  LabelType,
  MODAL_TYPES,
  useGlobalModalContext,
} from '../_ui';
import { Form } from '../_ui/Form.component';
import { Tooltip, TooltipContent, TooltipTrigger } from '../_ui/Tooltip.component';
import { CompanyEditor } from './CompanyEditor.component';

interface CompanyInfoProp {
  company: Company | undefined;
  isCompanyLoading: boolean;
  // Determines if any user is present within company
  isUserPresent: boolean;
  feePackage?: FeePackage;
  refetchCompany: () => Promise<any>;
}

export const CompanyInfo = ({
  company,
  feePackage,
  isCompanyLoading = false,
  isUserPresent = false,
  refetchCompany,
}: CompanyInfoProp) => {
  const { companies: companylist } = useCompany();
  const [sendReminder, sendReminderQueryResult] = useSendBankInfoReminderMutation();
  const { navigateWithCompanyId } = useNavigateWithSearchParam();

  /* Finding the channel Partner name from the companylist array. */
  const ChannelPartnerName = companylist?.find(
    (ele) => ele?.id === company?.businessInfo?.channelPartnerId,
  );
  const isViewingChannelPartner = company?.id === ChannelPartnerName?.id;
  const auth = useAuth();
  const { canEditCompany, canEditBillingInfo, canSendBankInfoReminder } = useCompanyPermissions(
    company?.id || undefined,
  );

  const { t } = useTranslation();
  const { showModal } = useGlobalModalContext();

  const handleEditClick = () => {
    if (!company) {
      return;
    }
    showModal(MODAL_TYPES.INFO_MODAL, {
      title: t('company_edit_detail'),
      width: '540px',
      height: 'max-content',
      onRenderBody: () => <CompanyEditor company={company} />,
      shouldCloseOnOverlayClick: false,
    });
  };

  const address = useMemo(() => {
    let result = '';
    if (company?.street) {
      const unit = company.unit ? `${company.unit}-` : '';
      result = `${unit}${company.streetNumber} ${company?.street}, ${company?.city}, ${company?.stateOrRegion}, ${company?.isoCountryCode}`;
    }
    return result;
  }, [company]);

  const serviceFeeText = useMemo(() => {
    if (!company) return '';

    const percent =
      company.transactionFeePercentage !== undefined &&
      company.transactionFeePercentage !== null &&
      company.transactionFeePercentage !== ''
        ? `${company.transactionFeePercentage}%`
        : '0%';

    const dollar =
      company.transactionFeeDollar !== undefined &&
      company.transactionFeeDollar !== null &&
      company.transactionFeeDollar !== ''
        ? convertToLocaleCurrency(Number(company.transactionFeeDollar), company.currency)
        : convertToLocaleCurrency(0, company.currency);

    // Flex plan shows only percentage
    if (feePackage?.type === PackageType.FLEX_PLAN) {
      return percent;
    }

    // All other plans: 5% plus $12.00
    return `${percent} ${t('plus')} ${dollar}`;
  }, [company, feePackage, t]);

  const getPlanLabel = (): string => {
    if (!feePackage?.type) return t('none');
    const translationKeyMap: Record<PackageType, string> = {
      [PackageType.NONE]: 'none',
      [PackageType.FLEX_PLAN]: 'flex_plan',
      [PackageType.BASIC_PLAN]: 'basic_plan',
      [PackageType.PRO_PLAN]: 'pro_plan',
      [PackageType.ELA]: 'ela',
      [PackageType.ESSENTIALS]: 'essential',
      [PackageType.ESSENTIALS_PLUS]: 'essentials_plus',
    };

    return t(translationKeyMap[feePackage.type]);
  };

  const onExternalLinkUpdateBankingInfoClick = () => {
    window.open(
      `https://chargelab.co/chargelab-forms/banking-information?dashboard_company_id=${company?.id}`,
    );
  };

  const onExternalLinkToHubSpotClick = () => {
    window.open(
      `https://app.hubspot.com/contacts/39848715/record/0-2/${company?.hubSpotCompanyRecordId}`,
    );
  };
  /**
   * Handles the click event on the send reminder.
   * This function triggers the `sendBankInfoReminder` api, which sends a reminder
   * to all the users of company regarding their missing bank information.
   */
  const onClickSendReminder = () => {
    sendReminder({
      companyId: company?.id,
      companyName: company?.name,
    } as sendBankInfoReminderPayload);
  };

  /**
   * Determines the disabled state of the 'Send Reminder' icon & label based on three conditions.
   * 1. Checks if a user is not present in company.
   * 2. Assesses if the send reminder query operation was successful
   * 3. Confirms that the company has provided bank information
   * The button is disabled if any of these conditions are true,
   * indicating either a lack of user, a successful reminder operation, or the presence of bank information.
   *
   * @returns {boolean} true if the send reminder should be disabled, false otherwise.
   */
  const disableSendReminderBtn = (): boolean => {
    return (
      !isUserPresent ||
      sendReminderQueryResult.isSuccess ||
      company?.bankInfoProvided !== BankInfoProvided.MISSING
    );
  };

  const handleUpdateBankingInfoExternalLinkDiv = () => {
    const canEditBilling = auth.role === USER_ROLE.SUPPORT || canEditBillingInfo;
    if (
      isCompanyLoading ||
      company?.bankInfoProvided === BankInfoProvided.NOT_REQUIRED ||
      !canEditBilling
    ) {
      return null;
    }
    return (
      <div className='flex flex-row items-center gap-1'>
        <Icon
          src={ExternalLink}
          size={IconSize.SIZE_16x16}
          color={ColorType.BRAND2}
          onClick={onExternalLinkUpdateBankingInfoClick}
          alt={t('icon_alt_update_info_new_window')}
        />
        <Label
          text={
            company?.bankInfoProvided === BankInfoProvided.PROVIDED
              ? t('banking_info_update_info_link')
              : t('banking_info_provide_info_link')
          }
          type={LabelType.LABEL_S_MEDIUM}
          color={ColorType.BRAND2}
          onClick={onExternalLinkUpdateBankingInfoClick}
        />
      </div>
    );
  };

  const [updateSync, syncQueryResult] = useSyncWithHubSpotMutation();

  const handleSync = () => {
    if (company?.id) {
      updateSync(company.id);
    }
  };

  useEffect(() => {
    if (syncQueryResult.isSuccess) {
      refetchCompany();
    }
  }, [syncQueryResult.isSuccess]);

  // handle Sync with HubSpot button
  const handleSyncWithHubSpot = () => {
    const disabled = isCompanyLoading || !company?.hubSpotCompanyRecordId;

    return (
      <Form onSubmit={handleSync} queryResult={syncQueryResult}>
        <div className='flex flex-row w-[148px] items-center justify-end'>
          <Tooltip placement='top' tipOffset={{ mainAxis: 10, crossAxis: 0 }}>
            <TooltipTrigger asChild>
              <span style={{ display: 'inline-block' }}>
                <Button
                  className='min-w-[160px]'
                  label={t('sync_with_hubspot')}
                  disabled={disabled}
                  type={ButtonType.SECONDARY}
                  size={ButtonSize.SMALL}
                  isSumbit
                  dataTestId='syncWithHubSpotButton'
                />
              </span>
            </TooltipTrigger>

            {disabled && (
              <TooltipContent>
                <Label
                  text={t('enter_hubspot_record_id_first')}
                  type={LabelType.BODY3}
                  color={ColorType.WHITE}
                />
              </TooltipContent>
            )}
          </Tooltip>
        </div>
      </Form>
    );
  };

  const handleExternalLinkViewToHubSpotDiv = () => {
    const text = company?.hubSpotCompanyRecordId ? t('icon_open_hubspot') : t('icon_goto_hubspot');
    return (
      <div className='flex flex-row items-center gap-1'>
        <Icon
          src={ExternalLink}
          size={IconSize.SIZE_16x16}
          color={ColorType.BRAND2}
          onClick={onExternalLinkToHubSpotClick}
          alt={text}
        />
        <Label
          text={text}
          type={LabelType.LABEL_S_MEDIUM}
          color={ColorType.BRAND2}
          onClick={onExternalLinkToHubSpotClick}
        />
      </div>
    );
  };

  const handleSendReminderDiv = () => {
    const canSendReminder = auth.role === USER_ROLE.SUPPORT || canSendBankInfoReminder;
    if (
      isCompanyLoading ||
      !canSendReminder ||
      company?.bankInfoProvided !== BankInfoProvided.MISSING
    ) {
      return null;
    }
    return (
      <div className='flex flex-row items-center gap-1 pl-4'>
        <Icon
          src={EmailOutline}
          size={IconSize.SIZE_16x16}
          color={disableSendReminderBtn() ? ColorType.GREY3 : ColorType.BRAND2}
          onClick={disableSendReminderBtn() ? undefined : onClickSendReminder}
        />
        <Label
          text={
            !isUserPresent
              ? t('banking_info_reminder')
              : disableSendReminderBtn()
              ? t('banking_info_sent_reminder')
              : t('banking_info_reminder')
          }
          type={LabelType.LABEL_S_MEDIUM}
          color={disableSendReminderBtn() ? ColorType.GREY3 : ColorType.BRAND2}
          onClick={disableSendReminderBtn() ? undefined : onClickSendReminder}
        />
      </div>
    );
  };

  /**
   * Determines the label for the bank information based on the company's bank info status.
   * The function checks the status of bank information provided by the company and returns
   * either MISSING, NOT REQUIRED or PROVIDED.
   * If the status is not one of the above, an empty string is returned.
   *
   * @returns {string} The internationalized label for the company's bank information status.
   */
  const handleBankInfoLabel = (): string => {
    switch (company?.bankInfoProvided) {
      case BankInfoProvided.MISSING:
        return t('banking_info_not_present');
      case BankInfoProvided.NOT_REQUIRED:
        return t('banking_info_not_required');
      case BankInfoProvided.PROVIDED:
        return t('banking_info_present');
      default:
        return '';
    }
  };

  if (!company) return null;

  return (
    <Card className='border-2 border-grey2'>
      <div className='flex flex-col gap-[8px] w-full'>
        <div className='flex flex-row items-start justify-between'>
          <div className='flex flex-row gap-10'>
            <div className='flex flex-col gap-[8px]'>
              <div className='flex flex-row gap-4 items-baseline'>
                <Label
                  className='min-w-[154px] max-w-[88px]'
                  text={t('company_name')}
                  type={LabelType.LABEL_M}
                  color={ColorType.GREY6}
                />
                <Label
                  text={company?.name}
                  type={LabelType.BODY3}
                  color={ColorType.BLACK}
                  isLoading={isCompanyLoading}
                  skeletonHeight='h-5'
                />
              </div>
              <div className='flex flex-row gap-4 items-baseline min-w-[50%]'>
                <Label
                  className='min-w-[154px] max-w-[88px]'
                  text={t('company_address')}
                  type={LabelType.LABEL_M}
                  color={ColorType.GREY6}
                />
                <Label
                  text={address}
                  type={LabelType.BODY3}
                  color={ColorType.BLACK}
                  isLoading={isCompanyLoading}
                  skeletonHeight='h-5'
                />
              </div>
              {auth.role === USER_ROLE.SUPPORT && (
                <>
                  <div className='flex flex-row gap-4 items-baseline'>
                    <Label
                      className='min-w-[154px] max-w-[154px]'
                      text={t('plan')}
                      type={LabelType.LABEL_M}
                      color={ColorType.GREY6}
                    />
                    <Label
                      text={getPlanLabel()}
                      type={LabelType.BODY3}
                      color={ColorType.BLACK}
                      isLoading={isCompanyLoading}
                      skeletonHeight='h-5'
                    />
                  </div>
                  <div className='flex flex-row gap-4 items-baseline'>
                    <Label
                      className='min-w-[154px] max-w-[154px]'
                      text={t('company_service_fee')}
                      type={LabelType.LABEL_M}
                      color={ColorType.GREY6}
                    />
                    <Label
                      text={serviceFeeText}
                      type={LabelType.BODY3}
                      color={ColorType.BLACK}
                      isLoading={isCompanyLoading}
                      skeletonHeight='h-5'
                    />
                  </div>
                  {auth.role === USER_ROLE.SUPPORT &&
                    feePackage?.type !== PackageType.BASIC_PLAN &&
                    feePackage?.type !== PackageType.PRO_PLAN && (
                      <div className='flex flex-row gap-4 items-baseline'>
                        <Label
                          className='min-w-[154px] max-w-[154px]'
                          text={t('driver_session_fee_dollar')}
                          type={LabelType.LABEL_M}
                          color={ColorType.GREY6}
                        />

                        <Label
                          text={convertToLocaleCurrency(
                            company.driverSessionFee !== undefined &&
                              company.driverSessionFee !== null &&
                              company.driverSessionFee !== ''
                              ? Number(company.driverSessionFee)
                              : 0,
                            company.currency,
                          )}
                          type={LabelType.BODY3}
                          color={ColorType.BLACK}
                          isLoading={isCompanyLoading}
                          skeletonHeight='h-5'
                        />
                      </div>
                    )}
                  <div className='flex flex-row gap-4 items-baseline'>
                    <Label
                      className='min-w-[154px] max-w-[88px]'
                      text={t('company_ela_status')}
                      type={LabelType.LABEL_M}
                      color={ColorType.GREY6}
                    />
                    <Label
                      text={company?.businessInfo?.ela ? t('yes') : t('no')}
                      type={LabelType.BODY3}
                      color={ColorType.BLACK}
                      isLoading={isCompanyLoading}
                      skeletonHeight='h-5'
                    />
                  </div>
                </>
              )}
              <div className='flex flex-row gap-4 items-baseline'>
                <Label
                  className='min-w-[154px] max-w-[156px]'
                  text={t('banking_info')}
                  type={LabelType.LABEL_M}
                  color={ColorType.GREY6}
                />
                <div className='flex flex-col gap-1'>
                  <div className='flex flex-col lg:flex-row lg:items-center gap-2'>
                    <div className='flex flex-row gap-2 items-center'>
                      {company?.bankInfoProvided === BankInfoProvided.MISSING &&
                        !isCompanyLoading && (
                          <Icon
                            src={ErrorWarningCircle}
                            size={IconSize.SIZE_16x16}
                            alt={t('icon_alt_alert')}
                          />
                        )}
                      <Label
                        text={handleBankInfoLabel()}
                        type={
                          company?.bankInfoProvided !== BankInfoProvided.MISSING
                            ? LabelType.BODY3
                            : LabelType.LABEL_S_MEDIUM
                        }
                        color={
                          company?.bankInfoProvided !== BankInfoProvided.MISSING
                            ? ColorType.BLACK
                            : ColorType.NEGATIVE
                        }
                        isLoading={isCompanyLoading}
                        skeletonHeight='h-5'
                      />
                    </div>
                    {/* Unified Links Container: No more double calling! */}
                    <div className='flex flex-row lg:pl-2 items-center gap-2'>
                      {handleUpdateBankingInfoExternalLinkDiv()}
                      {handleSendReminderDiv()}
                    </div>
                  </div>
                  {company?.bankInfoProvided === BankInfoProvided.MISSING && (
                    <Label
                      text={t('banking_info_message')}
                      type={LabelType.LABEL_XS_MEDIUM}
                      color={ColorType.GREY5}
                      isLoading={isCompanyLoading}
                      skeletonHeight='h-5'
                    />
                  )}
                </div>
              </div>
              {auth.role === USER_ROLE.SUPPORT && (
                <div className='flex flex-row gap-4 items-baseline'>
                  <Label
                    className='min-w-[154px] max-w-[156px]'
                    text={t('tax_id')}
                    type={LabelType.LABEL_M}
                    color={ColorType.GREY6}
                  />
                  <Label
                    text={
                      company?.companyTaxId !== undefined
                        ? company.companyTaxId
                        : t('not_yet_entered_in_hubspot')
                    }
                    type={LabelType.BODY3}
                    color={ColorType.BLACK}
                    isLoading={isCompanyLoading}
                    skeletonHeight='h-5'
                  />
                </div>
              )}
            </div>
            <div className='flex flex-col flex-grow gap-[8px]'>
              <div className='flex flex-row gap-4 items-baseline'>
                <Label
                  className='w-[110px]'
                  text={t('company_phone')}
                  type={LabelType.LABEL_M}
                  color={ColorType.GREY6}
                />
                <Label
                  text={company?.contactPhone}
                  type={LabelType.BODY3}
                  color={ColorType.BLACK}
                  isLoading={isCompanyLoading}
                  skeletonHeight='h-5'
                />
              </div>
              <div className='flex flex-row gap-4 items-baseline'>
                <Label
                  className='w-[110px]'
                  text={t('company_email')}
                  type={LabelType.LABEL_M}
                  color={ColorType.GREY6}
                />
                <Label
                  text={company?.contactEmail}
                  type={LabelType.BODY3}
                  color={ColorType.BLACK}
                  isLoading={isCompanyLoading}
                  skeletonHeight='h-5'
                />
              </div>
              {auth.role === USER_ROLE.SUPPORT && (
                <>
                  <div className='flex flex-row gap-4 items-baseline'>
                    <Label
                      className='w-[110px]'
                      text={t('company_network')}
                      type={LabelType.LABEL_M}
                      color={ColorType.GREY6}
                    />
                    <Label
                      text={company.whiteLabellingId || ''}
                      type={LabelType.BODY3}
                      color={ColorType.BLACK}
                      isLoading={isCompanyLoading}
                      skeletonHeight='h-5'
                    />
                  </div>
                  <div className='flex flex-row gap-4 items-baseline'>
                    <Label
                      className='w-[110px]'
                      text={t('company_partner')}
                      type={LabelType.LABEL_M}
                      color={ColorType.GREY6}
                    />
                    {ChannelPartnerName?.name ? (
                      isViewingChannelPartner ? (
                        <Label
                          text={ChannelPartnerName.name}
                          type={LabelType.BODY3}
                          color={ColorType.BLACK}
                          isLoading={isCompanyLoading}
                          skeletonHeight='h-5'
                        />
                      ) : (
                        <Label
                          text={ChannelPartnerName.name}
                          type={LabelType.BODY3}
                          color={ColorType.BRAND2}
                          isLoading={isCompanyLoading}
                          skeletonHeight='h-5'
                          onClick={() => navigateWithCompanyId('/company', ChannelPartnerName.id)}
                          className='cursor-pointer'
                        />
                      )
                    ) : (
                      <Label
                        text={t('none')}
                        type={LabelType.BODY3}
                        color={ColorType.BLACK}
                        isLoading={isCompanyLoading}
                        skeletonHeight='h-5'
                      />
                    )}
                  </div>
                  <div className='flex flex-row gap-4 items-baseline'>
                    <Label
                      className='w-[110px]'
                      text={t('company_test_company')}
                      type={LabelType.LABEL_M}
                      color={ColorType.GREY6}
                    />
                    <Label
                      text={company?.businessInfo?.testCompany ? t('yes') : t('no')}
                      type={LabelType.BODY3}
                      color={ColorType.BLACK}
                      isLoading={isCompanyLoading}
                      skeletonHeight='h-5'
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {canEditCompany && (
            <div className='flex flex-row w-[148px] items-center justify-end'>
              <Button
                label={t('company_edit_detail')}
                onClick={handleEditClick}
                type={ButtonType.TERTIARY}
                size={ButtonSize.SMALL}
                dataTestId='editCompanyButton'
              />
            </div>
          )}
        </div>
        {auth.role === USER_ROLE.SUPPORT && (
          <div className='pb-5'>
            <div className='flex flex-row items-baseline justify-between w-full h-[20px] -mt-2'>
              <div className='flex flex-row gap-4 items-baseline'>
                <Label
                  className='w-[154px]'
                  text={t('hubspot_company_record_id')}
                  type={LabelType.LABEL_M}
                  color={ColorType.GREY6}
                />
                <Label
                  text={company?.hubSpotCompanyRecordId ?? t('not_yet_entered_text')}
                  type={LabelType.BODY3}
                  color={ColorType.BLACK}
                  isLoading={isCompanyLoading}
                  skeletonHeight='h-5'
                />
              </div>
              {/* Buttons aligned to the right edge of the card */}
              <div className='flex flex-row gap-6 items-center'>
                {handleExternalLinkViewToHubSpotDiv()}
                {handleSyncWithHubSpot()}
              </div>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
